// Auto-Pause Rule Card Component (S010)
import React from 'react';
import type { AutoPauseRule } from '../types/auto-pause';
import { PerformanceBadge } from './PerformanceBadge';

interface AutoPauseRuleCardProps {
  rule: AutoPauseRule;
  onEdit: (rule: AutoPauseRule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, isActive: boolean) => void;
  onTest: (id: string) => void;
}

export const AutoPauseRuleCard: React.FC<AutoPauseRuleCardProps> = ({
  rule,
  onEdit,
  onDelete,
  onToggle,
  onTest,
}) => {
  const getConditionLabel = () => {
    const labels = {
      spend: 'Spend',
      roas: 'ROAS',
      ctr: 'CTR',
      cpc: 'CPC',
      cpm: 'CPM',
    };
    const operators = {
      gt: '>',
      lt: '<',
      eq: '=',
      gte: '≥',
      lte: '≤',
    };
    return `${labels[rule.condition_type]} ${operators[rule.operator]} ${rule.threshold_value}`;
  };

  return (
    <div className="bg-white dark:bg-boxdark rounded-lg shadow p-6 border border-stroke dark:border-strokedark">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-black dark:text-white">
              {rule.name}
            </h3>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              rule.is_active
                ? 'bg-success bg-opacity-10 text-success'
                : 'bg-bodydark1 bg-opacity-10 text-bodydark1'
            }`}>
              {rule.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          {rule.description && (
            <p className="text-sm text-bodydark dark:text-bodydark1 mb-4">
              {rule.description}
            </p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-bodydark dark:text-bodydark1">Condition:</span>
              <p className="font-medium text-black dark:text-white mt-1">
                {getConditionLabel()}
              </p>
            </div>
            
            <div>
              <span className="text-bodydark dark:text-bodydark1">Time Window:</span>
              <p className="font-medium text-black dark:text-white mt-1">
                {rule.time_window_hours}h
              </p>
            </div>
            
            <div>
              <span className="text-bodydark dark:text-bodydark1">Action:</span>
              <p className="font-medium text-black dark:text-white mt-1">
                {rule.action_type === 'pause' ? 'Auto-Pause' : 'Alert Only'}
              </p>
            </div>
            
            <div>
              <span className="text-bodydark dark:text-bodydark1">Platforms:</span>
              <p className="font-medium text-black dark:text-white mt-1">
                {rule.platforms.join(', ')}
              </p>
            </div>
          </div>

          {rule.trigger_count > 0 && (
            <div className="mt-4 text-sm">
              <span className="text-bodydark dark:text-bodydark1">
                Triggered {rule.trigger_count} times
              </span>
              {rule.last_triggered_at && (
                <span className="text-bodydark dark:text-bodydark1 ml-2">
                  • Last: {new Date(rule.last_triggered_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => onToggle(rule.id, !rule.is_active)}
            className={`px-3 py-1 rounded text-sm font-medium transition ${
              rule.is_active
                ? 'bg-success text-white hover:bg-opacity-90'
                : 'bg-bodydark1 text-white hover:bg-opacity-80'
            }`}
            title={rule.is_active ? 'Disable' : 'Enable'}
          >
            {rule.is_active ? 'ON' : 'OFF'}
          </button>
          
          <button
            onClick={() => onTest(rule.id)}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-meta-4 transition"
            title="Test Rule"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
          
          <button
            onClick={() => onEdit(rule)}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-meta-4 transition"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          
          <button
            onClick={() => onDelete(rule.id)}
            className="p-2 rounded hover:bg-red-50 dark:hover:bg-meta-1 text-red-600 transition"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
